import React, { useState } from 'react';
import { X, CheckCircle, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { trials } from '../utils/api';
import { toast } from 'sonner';

const DIMENSIONS = [
    {
        key: 'problem_solving',
        title: 'Problem Solving',
        ratingQuestion: 'How effectively did the candidate approach unfamiliar tasks or challenges during the trial?',
        ratingLabels: [
            'Struggled without guidance',
            'Required frequent help',
            'Managed basic problems independently',
            'Solved problems confidently',
            'Exceptional problem solver'
        ],
        behaviourQuestion: 'Did the candidate show initiative in resolving issues?',
        behaviourOptions: ['No', 'Occasionally', 'Yes', 'Consistently']
    },
    {
        key: 'execution_delivery',
        title: 'Execution & Delivery',
        ratingQuestion: 'How well did the candidate complete the tasks assigned during the trial?',
        ratingLabels: [
            'Tasks not completed',
            'Inconsistent output',
            'Completed expected work',
            'Delivered strong results',
            'Exceeded expectations'
        ],
        behaviourQuestion: 'How reliable was their output?',
        behaviourOptions: ['Missed tasks', 'Completed some tasks', 'Completed assigned tasks', 'Completed tasks efficiently']
    },
    {
        key: 'learning_speed',
        title: 'Learning Speed',
        ratingQuestion: 'How quickly did the candidate pick up new instructions or processes?',
        ratingLabels: [
            'Needed repeated explanation',
            'Slow to pick up tasks',
            'Average learning speed',
            'Picked things up quickly',
            'Extremely fast learner'
        ],
        behaviourQuestion: 'After receiving instructions, the candidate:',
        behaviourOptions: ['Needed repeated guidance', 'Improved gradually', 'Became confident quickly', 'Was operating independently very quickly']
    },
    {
        key: 'ownership_initiative',
        title: 'Ownership & Initiative',
        ratingQuestion: 'To what extent did the candidate take ownership of their work?',
        ratingLabels: [
            'Passive / waited for direction',
            'Minimal initiative',
            'Completed tasks when asked',
            'Proactively took responsibility',
            'Took strong ownership and drove tasks forward'
        ],
        behaviourQuestion: 'Did the candidate show initiative beyond assigned tasks?',
        behaviourOptions: ['No', 'Slightly', 'Yes', 'Significantly']
    },
    {
        key: 'collaboration',
        title: 'Collaboration',
        ratingQuestion: 'How well did the candidate work with the team during the trial?',
        ratingLabels: [
            'Struggled to work with others',
            'Limited interaction',
            'Worked well when required',
            'Positive team contributor',
            'Excellent team fit'
        ],
        behaviourQuestion: 'Team interaction felt:',
        behaviourOptions: ['Difficult', 'Neutral', 'Positive', 'Highly collaborative']
    },
    {
        key: 'communication',
        title: 'Communication',
        ratingQuestion: 'How clearly did the candidate communicate during the trial?',
        ratingLabels: [
            'Communication issues',
            'Sometimes unclear',
            'Clear enough',
            'Strong communicator',
            'Exceptionally clear and confident'
        ],
        behaviourQuestion: "The candidate's communication was:",
        behaviourOptions: ['Confusing', 'Basic', 'Clear', 'Very clear and professional']
    }
];

const OVERALL_OPTIONS = ['Not suitable', 'Below expectations', 'Met expectations', 'Strong performer', 'Exceptional'];
const OVERALL_VALUES = ['not_suitable', 'below_expectations', 'met_expectations', 'strong_performer', 'exceptional'];

const HIRING_OPTIONS = ['No', 'Possibly', 'Yes', 'Strong yes'];
const HIRING_VALUES = ['no', 'possibly', 'yes', 'strong_yes'];

const TrialFeedbackForm = ({ isOpen, onClose, trial, onComplete }) => {
    const [step, setStep] = useState(0); // 0-5 = dimensions, 6 = final
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    
    const [answers, setAnswers] = useState(
        DIMENSIONS.reduce((acc, dim) => ({
            ...acc,
            [`${dim.key}_rating`]: null,
            [`${dim.key}_behaviour`]: null,
        }), {
            overall_impression: null,
            hiring_intent: null,
            overall_notes: ''
        })
    );

    if (!isOpen || !trial) return null;

    const currentDim = DIMENSIONS[step];
    const totalSteps = DIMENSIONS.length + 1; // 6 dimensions + final
    const progress = ((step + 1) / totalSteps) * 100;

    const setAnswer = (key, value) => {
        setAnswers(prev => ({ ...prev, [key]: value }));
    };

    const canProceed = () => {
        if (step < 6) {
            return answers[`${currentDim.key}_rating`] !== null && answers[`${currentDim.key}_behaviour`] !== null;
        }
        return answers.overall_impression !== null && answers.hiring_intent !== null;
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const payload = {
                trial_id: trial.id,
                ...answers,
            };
            const res = await trials.submitFeedback(payload);
            setResult(res.data);
            toast.success('Feedback submitted successfully');
        } catch (err) {
            toast.error(err.userMessage || 'Failed to submit feedback');
        } finally {
            setSubmitting(false);
        }
    };

    // Success state
    if (result) {
        return (
            <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl p-8 text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="h-10 w-10 text-green-600" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                        Feedback Submitted
                    </h2>
                    <p className="text-gray-600 mb-4">Candidate's Snapshot Score has been updated.</p>
                    
                    <div className="bg-gray-50 rounded-xl p-4 mb-4 text-left">
                        <div className="flex justify-between text-sm mb-2">
                            <span className="text-gray-500">Snapshot Score</span>
                            <span className="font-bold text-purple-700" data-testid="post-trial-snapshot-score">{result.post_trial_snapshot_score}/100</span>
                        </div>
                        {result.wildcard_triggered && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-800">
                                Wildcard status triggered by exceptional trial performance
                            </div>
                        )}
                    </div>
                    
                    {result.ai_summary && (
                        <div className="bg-purple-50 rounded-xl p-4 mb-4 text-left">
                            <p className="text-xs font-semibold text-purple-600 mb-1">AI Trial Summary</p>
                            <p className="text-sm text-gray-700">{result.ai_summary}</p>
                        </div>
                    )}
                    
                    <Button
                        onClick={() => { onComplete?.(); onClose(); }}
                        className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                        data-testid="feedback-done-btn"
                    >
                        Done
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
                {/* Header */}
                <div className="p-5 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl z-10">
                    <div>
                        <h3 className="text-lg font-bold" style={{ color: '#1E3A5F', fontFamily: 'Unbounded, sans-serif' }}>
                            Trial Feedback
                        </h3>
                        <p className="text-xs text-gray-500 mt-0.5">Step {step + 1} of {totalSteps}</p>
                    </div>
                    <Button variant="ghost" onClick={onClose} className="p-1" data-testid="close-feedback-btn">
                        <X className="h-5 w-5" />
                    </Button>
                </div>

                {/* Progress bar */}
                <div className="h-1 bg-gray-100">
                    <div className="h-full bg-purple-600 transition-all duration-300" style={{ width: `${progress}%` }} />
                </div>

                <div className="p-5">
                    {step === 0 && (
                        <p className="text-sm text-gray-600 mb-4 p-3 bg-gray-50 rounded-lg">
                            Please rate the candidate's performance during the trial shift. Your feedback helps improve talent insights and ensures fair assessment.
                            <span className="block mt-1 font-medium text-gray-500">Estimated time: 1 minute</span>
                        </p>
                    )}

                    {/* Dimension questions (steps 0-5) */}
                    {step < 6 && (
                        <div>
                            <h4 className="font-bold text-gray-900 mb-4 text-base">
                                {step + 1}. {currentDim.title}
                            </h4>

                            {/* Rating question */}
                            <p className="text-sm text-gray-700 mb-3">{currentDim.ratingQuestion}</p>
                            <div className="space-y-2 mb-6">
                                {currentDim.ratingLabels.map((label, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setAnswer(`${currentDim.key}_rating`, i + 1)}
                                        className={`w-full text-left px-4 py-2.5 rounded-lg text-sm transition-all border ${
                                            answers[`${currentDim.key}_rating`] === i + 1
                                                ? 'bg-purple-50 border-purple-400 text-purple-900 font-medium'
                                                : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                                        }`}
                                        data-testid={`rating-${currentDim.key}-${i + 1}`}
                                    >
                                        <span className="font-semibold text-xs text-gray-400 mr-2">{i + 1}</span>
                                        {label}
                                    </button>
                                ))}
                            </div>

                            {/* Behaviour signal */}
                            <p className="text-sm text-gray-700 mb-3 font-medium">{currentDim.behaviourQuestion}</p>
                            <div className="grid grid-cols-2 gap-2">
                                {currentDim.behaviourOptions.map((opt, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setAnswer(`${currentDim.key}_behaviour`, i)}
                                        className={`px-3 py-2 rounded-lg text-sm transition-all border text-center ${
                                            answers[`${currentDim.key}_behaviour`] === i
                                                ? 'bg-teal-50 border-teal-400 text-teal-900 font-medium'
                                                : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                                        }`}
                                        data-testid={`behaviour-${currentDim.key}-${i}`}
                                    >
                                        {opt}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Final section (step 6) */}
                    {step === 6 && (
                        <div>
                            <h4 className="font-bold text-gray-900 mb-4 text-base">Overall Assessment</h4>

                            {/* Overall impression */}
                            <p className="text-sm text-gray-700 mb-3">How would you describe the candidate's overall performance?</p>
                            <div className="space-y-2 mb-6">
                                {OVERALL_OPTIONS.map((opt, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setAnswer('overall_impression', OVERALL_VALUES[i])}
                                        className={`w-full text-left px-4 py-2.5 rounded-lg text-sm transition-all border ${
                                            answers.overall_impression === OVERALL_VALUES[i]
                                                ? 'bg-purple-50 border-purple-400 text-purple-900 font-medium'
                                                : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                                        }`}
                                        data-testid={`overall-${OVERALL_VALUES[i]}`}
                                    >
                                        {opt}
                                    </button>
                                ))}
                            </div>

                            {/* Hiring intent */}
                            <p className="text-sm text-gray-700 mb-3 font-medium">Would you consider hiring this candidate?</p>
                            <div className="grid grid-cols-2 gap-2 mb-6">
                                {HIRING_OPTIONS.map((opt, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setAnswer('hiring_intent', HIRING_VALUES[i])}
                                        className={`px-3 py-2.5 rounded-lg text-sm transition-all border text-center ${
                                            answers.hiring_intent === HIRING_VALUES[i]
                                                ? 'bg-teal-50 border-teal-400 text-teal-900 font-medium'
                                                : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                                        }`}
                                        data-testid={`hiring-${HIRING_VALUES[i]}`}
                                    >
                                        {opt}
                                    </button>
                                ))}
                            </div>

                            {/* Optional comment */}
                            <p className="text-sm text-gray-700 mb-2">Any additional notes? <span className="text-gray-400">(optional)</span></p>
                            <Textarea
                                value={answers.overall_notes}
                                onChange={(e) => setAnswer('overall_notes', e.target.value)}
                                placeholder="Short notes about the candidate's performance..."
                                rows={3}
                                className="text-sm"
                                data-testid="feedback-notes"
                            />
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-5 border-t border-gray-200 flex items-center justify-between sticky bottom-0 bg-white rounded-b-2xl">
                    <Button
                        variant="ghost"
                        onClick={() => step > 0 ? setStep(step - 1) : onClose()}
                        className="text-gray-500"
                        data-testid="feedback-back-btn"
                    >
                        {step === 0 ? 'Cancel' : 'Back'}
                    </Button>
                    
                    {step < 6 ? (
                        <Button
                            onClick={() => setStep(step + 1)}
                            disabled={!canProceed()}
                            className="bg-purple-600 hover:bg-purple-700 text-white"
                            data-testid="feedback-next-btn"
                        >
                            Next <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                    ) : (
                        <Button
                            onClick={handleSubmit}
                            disabled={!canProceed() || submitting}
                            className="bg-teal-600 hover:bg-teal-700 text-white"
                            data-testid="submit-feedback-btn"
                        >
                            {submitting ? 'Submitting...' : 'Submit Feedback'}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TrialFeedbackForm;
